import type { ThreadHistoryAdapter } from '@assistant-ui/react';
import type { CitationRef } from '@iq/contracts/v1/chat';
import { api } from './api';

interface PersistedRow {
  id: string;
  /**
   * Branch parent from `chat_messages.parent_id`. `null` on the very first
   * message of a thread; otherwise points at any earlier row in the same
   * thread. Multiple rows can share a `parentId`, which is how an
   * edit-induced branch produces siblings the runtime walks via
   * `BranchPickerPrimitive`. Optional on the wire because the field is
   * omitted entirely by server responses that pre-date this contract.
   */
  parentId?: string | null;
  role: 'user' | 'assistant' | 'system' | 'tool';
  parts: ReadonlyArray<Record<string, unknown>>;
  citations?: ReadonlyArray<CitationRef>;
}

interface UIMessagePart {
  type: string;
  [key: string]: unknown;
}

interface UIMessageLike {
  id: string;
  role: 'user' | 'assistant' | 'system';
  parts: UIMessagePart[];
}

/**
 * Translate one DB row into the AI-SDK `UIMessage` shape assistant-ui's
 * runtime stores. The `tool` role is collapsed onto `assistant` (assistant-ui
 * doesn't expose a tool-role message — tool calls/results live as parts
 * inside an assistant message). Citations are injected as a `data-citations`
 * data-part so the CitationDataUI registry picks them up on render.
 *
 * Persisted `file` parts (server schema `{type:'file', upload_id, mediaType,
 * filename}`) are rewritten to the AI SDK `FileUIPart` shape
 * (`{type:'file', mediaType, filename, url}`) before the runtime ingests
 * them — assistant-ui's `AISDKMessageConverter` uses `part.mediaType` +
 * `part.filename` + `part.url` to materialize the `<UserMessageAttachments />`
 * chips. The server's `upload_id` is stashed into `url` so a follow-up
 * `regenerate` can recover the attachment ref via
 * `prepareSendMessagesRequest` → `extractAttachmentsFromMessages`.
 */
function rowToUiMessage(row: PersistedRow): UIMessageLike {
  const role: UIMessageLike['role'] = row.role === 'tool' ? 'assistant' : row.role;
  const parts: UIMessagePart[] =
    Array.isArray(row.parts) && row.parts.length > 0
      ? row.parts.map((p) => rehydratePart(p as UIMessagePart))
      : [{ type: 'text', text: '' }];
  if (role === 'assistant' && Array.isArray(row.citations) && row.citations.length > 0) {
    parts.push({ type: 'data-citations', data: { citations: row.citations } });
  }
  return { id: row.id, role, parts };
}

/**
 * Translate any persisted `file` part to the AI SDK FileUIPart wire
 * shape. Other part types pass through unchanged.
 */
function rehydratePart(part: UIMessagePart): UIMessagePart {
  if (part.type !== 'file') return { ...part };
  const uploadId = typeof part.upload_id === 'string' ? part.upload_id : '';
  const mediaType =
    typeof part.mediaType === 'string'
      ? part.mediaType
      : typeof part.mimeType === 'string'
        ? part.mimeType
        : 'application/octet-stream';
  const filename = typeof part.filename === 'string' ? part.filename : 'file';
  return {
    type: 'file',
    mediaType,
    filename,
    url: uploadId,
  };
}

/**
 * Fetch raw rows from `/threads/:id/messages`. Returns `[]` on 401/404/error
 * so a deleted thread renders an empty composer rather than crashing the
 * runtime.
 */
async function fetchRows(threadId: string): Promise<PersistedRow[]> {
  const { data, error } = await api.GET('/threads/{threadId}/messages', {
    params: { path: { threadId } },
  });
  if (error) return [];
  return (data.messages ?? []) as unknown as PersistedRow[];
}

/**
 * Build a `ThreadHistoryAdapter` for the AI-SDK `useChatRuntime`. The base
 * adapter's `load`/`append` are unused on this path (per assistant-ui's docs
 * the AI SDK adapter MUST implement `withFormat`); the in-format adapter
 * built inside `withFormat` is the one the runtime actually drives.
 *
 * `threadIdRef` is a getter so the closure reads the live remoteId after
 * the runtime hops between threads (sidebar click, lazy-mint, etc.). The
 * runtime tears down and re-mounts on thread change so this is mostly
 * insurance against close-captured stale ids during the transition.
 *
 * `append` is intentionally a no-op — the streaming `/threads/:id/messages`
 * route persists every assistant turn server-side, so the client never
 * needs to push history back up.
 */
export function makeHistoryAdapter(threadIdRef: { current: string | null }): ThreadHistoryAdapter {
  return {
    async load() {
      return { messages: [] };
    },
    async append() {
      // Intentional no-op: the server persists assistant turns inside the
      // streaming route, so the client never pushes history back up.
    },
    withFormat: (fmt) => ({
      async load() {
        const id = threadIdRef.current;
        if (!id) return { messages: [] };
        const rows = await fetchRows(id);
        // Each row carries its own `parent_id` from the server, which is
        // how the AI-SDK message-format decoder feeds the runtime's
        // `MessageRepository` and how branched edits / reloads survive a
        // page refresh. Fall back to the previous row's id only on the
        // legacy contract where the field is absent.
        let prevId: string | null = null;
        const messages = rows.map((row) => {
          const ui = rowToUiMessage(row);
          const parentForDecode = row.parentId !== undefined ? (row.parentId ?? null) : prevId;
          const entry = {
            id: row.id,
            parent_id: parentForDecode,
            format: fmt.format,
            content: ui as unknown as Parameters<typeof fmt.decode>[0]['content'],
          };
          prevId = row.id;
          return fmt.decode(entry);
        });
        return { messages };
      },
      async append() {
        // Intentional no-op: server persistence makes pushing history back up unnecessary.
      },
    }),
  };
}
